// components/UseCases.js
import React, { useEffect, useRef } from "react";
import styled from "styled-components";

import jewelryShowrooms from "../assets/jewelry-showrooms.png";
import watchBoutiques from "../assets/watch-boutiques.png";
import fashionFlagships from "../assets/fashion-flagships.jpg";
import vipLounges from "../assets/vip-lounges.jpg";
import trunkShow from "../assets/trunk-show.png";

const SectionUseCaseLabel = styled.div`
  font-family: "Montserrat", sans-serif;
  font-weight: 600;
  font-size: 20px;
  letter-spacing: 2px;
  text-transform: uppercase;
  background: linear-gradient(
    106.71deg,
    #c69957 0%,
    #f7e096 47.6%,
    #a57632 93.27%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Headline = styled.h2`
  font-family: "Montserrat";
  font-weight: 700;
  font-size: 48px;
  line-height: 59px;
  color: #ffffff;
  text-align: center;
  margin: 16px 0;

  @media (max-width: 768px) {
    font-size: 32px;
    line-height: 42px;
  }

  @media (max-width: 480px) {
    font-size: 24px;
    line-height: 34px;
  }
`;
const CarouselContent = styled.div`
  display: flex;
  gap: 48px;
  min-width: max-content; // Add this back!
`;

const CardImage = styled.div`
  width: 361.5px;
  height: 375px;
  background-image: ${(props) => `url(${props.image})`};
  background-size: cover;
  background-position: center top -2px;
  border-radius: 24.75px;
  position: relative;
  transition: all 0.3s ease;
  overflow: visible;

  &::before {
    content: "";
    position: absolute;
    inset: -3px;
    border-radius: 24.75px;
    background: linear-gradient(
      258.66deg,
      #fffb90 2.12%,
      #fbec7a 14.62%,
      #f8dc65 25.19%,
      #c8a443 33.84%,
      #fae570 40.57%,
      #996c22 49.22%,
      #caaa4d 61.72%,
      #fbe878 68.45%,
      #fdf491 72.29%,
      #ffffaa 76.14%,
      #fdf491 79.02%,
      #fbe878 81.9%,
      #d0a549 90.56%,
      #a4631b 98.25%
    );
    -webkit-mask: linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    opacity: 0;
    transition: opacity 0.3s ease;
    pointer-events: none;
    z-index: -1;
    padding: 3px;
  }

  @media (max-width: 768px) {
    width: 240px;
    height: 260px;
    background-position: center top -1px;
  }

  @media (max-width: 480px) {
    width: 200px;
    height: 220px;
    background-position: center top -1px;
  }
`;

const Card = styled.div`
  flex: 0 0 auto;
  width: 361.5px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding-top: 20px; /* Allocate space instead of pulling image up */
  padding-left: 5px;
  padding-right: 5px;
  padding-bottom: 5px;

  &:hover ${CardImage}::before {
    opacity: 1;
  }

  &:hover ${CardImage} {
    transform: translateY(-5px);
    box-shadow: 0px 8px 24px rgba(0, 0, 0, 0.3);
  }

  @media (max-width: 768px) {
    width: 240px;
    padding-top: 16px;
  }

  @media (max-width: 480px) {
    width: 200px;
    padding-top: 12px;
  }
`;

const UseCaseSection = styled.section`
  background: #0c1c2c;
  overflow: hidden;
  position: relative;
  width: 100%;
  height: 837px;
  padding: 80px 0 32px;

  @media (max-width: 768px) {
    height: auto; /* Allow responsiveness on smaller screens */
    padding: 48px 0 24px;
  }

  @media (max-width: 480px) {
    height: auto;
    padding: 32px 0 16px;
  }
`;

const HeaderContainer = styled.div`
  max-width: 1320px;
  height: 146px;
  margin: 0 auto;
  padding: 0 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;

  @media (max-width: 768px) {
    height: 156px;
  }

  @media (max-width: 480px) {
    height: 166px;
  }
`;

const CarouselContainer = styled.div`
  width: calc(100vw - 80px); /* Full viewport width minus padding */
  margin-left: calc(-50vw + 50% + 40px); /* Center with padding offset */
  overflow: hidden;
  margin-top: 48px;

  @media (max-width: 768px) {
    width: 100%;
    margin-left: 0;
    padding: 0 16px;
  }
`;

const FullWidthCarousel = styled.div`
  display: flex;
  gap: 48px;
  overflow-x: auto;
  scroll-behavior: smooth;
  white-space: nowrap;
  width: 100%;

  &::-webkit-scrollbar {
    display: none;
  }

  scrollbar-width: none;
`;

const CardTitle = styled.div`
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  font-size: 24px;
  line-height: 36px;
  letter-spacing: 0.75px;
  text-align: center;
  vertical-align: middle;
  color: #ffffff;

  display: flex;
  align-items: center;
  justify-content: center;
  height: 36px;

  @media (max-width: 768px) {
    font-size: 20px;
    line-height: 32px;
  }

  @media (max-width: 480px) {
    font-size: 18px;
    line-height: 28px;
  }
`;

const cards = [
  { title: "Jewelry Showrooms", image: jewelryShowrooms },
  { title: "Watch Boutiques", image: watchBoutiques },
  { title: "Fashion Flagships", image: fashionFlagships },
  { title: "VIP lounges", image: vipLounges },
  { title: "Trunk shows & launches", image: trunkShow },
];

const UseCases = ({ data }) => {
  const carouselRef = useRef(null);

  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;

    let animationFrameId;
    const scrollSpeed = 10;

    const scroll = () => {
      if (!carousel) return;
      carousel.scrollLeft += scrollSpeed;
      const singleSetWidth = carousel.scrollWidth / 3;
      if (carousel.scrollLeft >= singleSetWidth * 2) {
        carousel.scrollLeft = 0;
      }
      animationFrameId = requestAnimationFrame(scroll);
    };

    const startDelay = setTimeout(() => {
      animationFrameId = requestAnimationFrame(scroll);
    }, 500);

    const pause = () => cancelAnimationFrame(animationFrameId);
    const resume = () => (animationFrameId = requestAnimationFrame(scroll));

    carousel.addEventListener("mouseenter", pause);
    carousel.addEventListener("mouseleave", resume);

    return () => {
      clearTimeout(startDelay);
      cancelAnimationFrame(animationFrameId);
      carousel.removeEventListener("mouseenter", pause);
      carousel.removeEventListener("mouseleave", resume);
    };
  }, []);

  return (
    <UseCaseSection>
      <HeaderContainer>
        <SectionUseCaseLabel>
          {data?.sectionLabel || "Market Applications"}
        </SectionUseCaseLabel>
        <Headline>{data?.headline || "Built for High-Value Spaces"}</Headline>
      </HeaderContainer>

      <CarouselContainer>
        <FullWidthCarousel ref={carouselRef}>
          <CarouselContent>
            {[...cards, ...cards, ...cards].map((card, i) => (
              <Card key={i}>
                <CardImage image={card.image} />
                <CardTitle>{card.title}</CardTitle>
              </Card>
            ))}
          </CarouselContent>
        </FullWidthCarousel>
      </CarouselContainer>
    </UseCaseSection>
  );
};

export default UseCases;
